import { useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { iniciarBossFinal } from '../api/bossFinal';
import { sortearInsightsMock } from '../cena/bossFinalInsightsMock.js';
import { PalcoDoBossFinal } from './PalcoDoBossFinal';
import type { InsightDaCena } from './PalcoDoBossFinal';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

interface BossFinalPainelProps {
    csrfToken?: string;
}

/** O que o operador lê quando o disparo não sai; o detalhe técnico fica na trilha do painel. */
function mensagemDoErro(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp) {
        if (erro.status === 409) return 'O Boss Final desta turma já foi encerrado.';
        if (erro.status === 401 || erro.status === 403) return 'Sessão expirada ou sem permissão. Entre de novo.';
        if (erro.status >= 500) return 'O jogo não respondeu. Tente de novo em instantes.';
    }
    return 'Não foi possível iniciar o Boss Final. Tente novamente.';
}

/**
 * Tela Boss Final: o operador escolhe a turma e dispara; a animação roda neste painel, em tela
 * cheia. Os destaques são sorteados de 100 mocks (sem a tabela de insights) por enquanto.
 */
export function BossFinalPainel({ csrfToken = '' }: BossFinalPainelProps = {}) {
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [iniciando, setIniciando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [insights, setInsights] = useState<InsightDaCena[] | null>(null);

    useEffect(() => {
        listarTurmas()
            .then((lista) => {
                const ativas = lista.filter((turma) => turma.ativa);
                setTurmas(ativas);
                setTurmaId((atual) => manterSeDisponivel(atual, ativas.map((turma) => turma.id), ativas[0]?.id ?? null));
            })
            .catch(() => setErro('Não foi possível carregar as turmas.'));
    }, []);

    async function iniciar() {
        if (!turmaId || iniciando) return;
        setIniciando(true);
        setErro(null);
        try {
            await iniciarBossFinal({ turmaId, csrfToken });
            // A cena usa `{ id, grupo, texto }`; o mock traz `grupo_id`.
            setInsights(sortearInsightsMock().map(({ id, grupo_id: grupo, texto }: { id: string; grupo_id: string; texto: string }) => ({ id, grupo, texto })));
        } catch (falha) {
            setErro(mensagemDoErro(falha));
        } finally {
            setIniciando(false);
        }
    }

    const fechar = useCallback(() => setInsights(null), []);

    return (
        <div className="boss-final">
            <p className="descricao-boss-final">
                Dispara o Boss Final da turma e exibe a animação aqui, em tela cheia. A fotografia dos destaques
                é criada na primeira vez; repetir o disparo reusa a mesma.
            </p>
            <div className="barra-turma-operacao">
                <label htmlFor="turma-boss-final">ID da Turma<span aria-hidden="true">:</span></label>
                <select id="turma-boss-final" value={turmaId ?? ''} onChange={(evento) => setTurmaId(evento.target.value)}>
                    {turmas.map((turma) => (
                        <option key={turma.id} value={turma.id}>{turma.id}</option>
                    ))}
                </select>
                <button
                    type="button"
                    className="botao-iniciar-boss-final"
                    onClick={() => void iniciar()}
                    disabled={!turmaId || iniciando}
                >
                    {iniciando ? 'Iniciando...' : 'Iniciar Boss Final'}
                </button>
            </div>
            {erro && <p className="erro-boss-final" role="alert">{erro}</p>}
            {insights && <PalcoDoBossFinal insights={insights} aoFechar={fechar} />}
        </div>
    );
}
